import React, { useState } from 'react';
import { Calendar, Clock, Video, Phone, MapPin, Plus, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateMeeting } from '@/hooks/api/useMeetings';
import { MeetingType } from '@/services/api/meetings.api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const meetingTypeConfig: Record<MeetingType, { label: string; icon: React.ElementType }> = {
  [MeetingType.VIDEO]: { label: 'Visioconférence', icon: Video },
  [MeetingType.PHONE]: { label: 'Téléphone', icon: Phone },
  [MeetingType.IN_PERSON]: { label: 'En personne', icon: MapPin },
};

interface MeetingSchedulerProps {
  className?: string;
  dealId?: string;
  onScheduled?: () => void;
}

export function MeetingScheduler({ className, dealId, onScheduled }: MeetingSchedulerProps) {
  const { toast } = useToast();
  const createMeeting = useCreateMeeting();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<MeetingType>(MeetingType.VIDEO);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [location, setLocation] = useState('');
  const [participants, setParticipants] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !date || !time) {
      toast({ title: 'Champs requis', description: 'Titre, date et heure sont obligatoires.', variant: 'destructive' });
      return;
    }

    const scheduledAt = new Date(`${date}T${time}`).toISOString();
    const participantList = participants.split(',').map(p => p.trim()).filter(Boolean);

    createMeeting.mutate(
      {
        title,
        description: description || undefined,
        type,
        scheduledAt,
        duration: `${duration}min`,
        dealId: dealId || undefined,
        participants: participantList,
        location: location || undefined,
      },
      {
        onSuccess: () => {
          toast({ title: 'Réunion planifiée', description: `"${title}" a été créée avec succès.` });
          setTitle('');
          setDescription('');
          setDate('');
          setTime('');
          setDuration('30');
          setLocation('');
          setParticipants('');
          onScheduled?.();
        },
        onError: () => {
          toast({ title: 'Erreur', description: 'Impossible de créer la réunion.', variant: 'destructive' });
        },
      }
    );
  };

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Planifier une réunion
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="meeting-title">Titre *</Label>
            <Input id="meeting-title" placeholder="Ex: Point d'avancement deal" value={title} onChange={e => setTitle(e.target.value)} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-type">Type</Label>
              <Select value={type} onValueChange={v => setType(v as MeetingType)}>
                <SelectTrigger id="meeting-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(meetingTypeConfig).map(([value, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <SelectItem key={value} value={value}>
                        <span className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {cfg.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meeting-duration">Durée</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger id="meeting-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="45">45 min</SelectItem>
                  <SelectItem value="60">1 heure</SelectItem>
                  <SelectItem value="90">1h30</SelectItem>
                  <SelectItem value="120">2 heures</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-date">Date *</Label>
              <Input id="meeting-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meeting-time">Heure *</Label>
              <Input id="meeting-time" type="time" value={time} onChange={e => setTime(e.target.value)} />
            </div>
          </div>

          {type === MeetingType.IN_PERSON && (
            <div className="space-y-2">
              <Label htmlFor="meeting-location">Lieu</Label>
              <Input id="meeting-location" placeholder="Adresse du rendez-vous" value={location} onChange={e => setLocation(e.target.value)} />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="meeting-participants">
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                Participants (emails séparés par des virgules)
              </span>
            </Label>
            <Input id="meeting-participants" placeholder="jean@example.com, marie@example.com" value={participants} onChange={e => setParticipants(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="meeting-desc">Description</Label>
            <Textarea id="meeting-desc" placeholder="Notes ou ordre du jour…" rows={3} value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <Button type="submit" className="w-full gap-2" disabled={createMeeting.isPending}>
            <Plus className="h-4 w-4" />
            {createMeeting.isPending ? 'Création…' : 'Planifier la réunion'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
